import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Upload, Check, CheckCircle2, AlertTriangle, Download } from "lucide-react";
import { importExcel, downloadTable } from "../../services/excelService";
import { fetchTables } from "../../services/sadtService";
import { toast } from "../../stores/toastStore";

// ── Pipeline stepper ──────────────────────────────────────────────────
const STEPS = [
  { id: 1, label: "Embedding document",    sub: "Chunking text for AI chat RAG" },
  { id: 2, label: "Ingesting data",         sub: "Writing structured tables to SQLite" },
  { id: 3, label: "Running calculators",    sub: "Computing load, overload, and risk metrics" },
  { id: 4, label: "Complete",               sub: "All pipeline steps finished" },
];

function Stepper({ currentStep }) {
  return (
    <div className="flex flex-col gap-5 py-2">
      {STEPS.map((step) => {
        const done    = currentStep > step.id;
        const active  = currentStep === step.id;
        const pending = currentStep < step.id;
        return (
          <div key={step.id} className="flex items-start gap-4">
            {/* Node */}
            <div className="relative mt-0.5 shrink-0" style={{ width: 20, height: 20 }}>
              {active && (
                <div
                  className="absolute inset-0 rounded-full animate-ping"
                  style={{ background: "var(--color-accent)", opacity: 0.25 }}
                />
              )}
              <div
                className="relative flex items-center justify-center rounded-full border"
                style={{
                  width: 20, height: 20,
                  borderColor: pending ? "var(--color-rule-strong)" : "var(--color-accent)",
                  background: done
                    ? "var(--color-accent)"
                    : active
                      ? "var(--color-accent-soft)"
                      : "transparent",
                }}
              >
                {done   && <Check size={11} color="white" strokeWidth={2.5} />}
                {active && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--color-accent)" }} />}
                {pending && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--color-rule-strong)" }} />}
              </div>
            </div>

            {/* Labels */}
            <div>
              <div
                style={{
                  fontSize: "var(--text-sm)",
                  fontWeight: active ? 500 : 400,
                  color: pending ? "var(--color-ink-3)" : "var(--color-ink)",
                  transition: "color 300ms",
                }}
              >
                {step.label}
              </div>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)", marginTop: 2 }}>
                {step.sub}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Drop zone ─────────────────────────────────────────────────────────
function DropZone({ onFile }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef(null);

  function handleDrop(e) {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className="border-2 border-dashed px-8 py-14 text-center cursor-pointer transition-colors"
      style={{
        borderColor: isDragOver ? "var(--color-accent)" : "var(--color-rule-strong)",
        background: isDragOver ? "var(--color-accent-soft)" : "transparent",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
      />
      <Upload
        size={28}
        className="mx-auto mb-3"
        style={{ color: isDragOver ? "var(--color-accent)" : "var(--color-ink-3)" }}
      />
      <p style={{ fontSize: "var(--text-base)", color: "var(--color-ink-2)" }}>
        {isDragOver ? "Release to upload" : "Drop your SADT workbook here"}
      </p>
      <p style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)", marginTop: 6 }}>
        .xlsx · .xls accepted
      </p>
      <button
        className="btn btn-ghost mt-5"
        type="button"
        onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
      >
        Choose file
      </button>
    </div>
  );
}

// ── Import result card ────────────────────────────────────────────────
function ImportResult({ result, onReset }) {
  const hasCalcErrors = Object.keys(result.calculation_errors || {}).length > 0;

  return (
    <div className="border border-[var(--color-rule)]">
      {/* Title bar */}
      <div className="px-6 py-4 border-b border-[var(--color-rule)] flex items-center gap-3">
        <CheckCircle2 size={17} style={{ color: "var(--color-status-even)", flexShrink: 0 }} />
        <span className="display" style={{ fontSize: "var(--text-xl)" }}>Import complete</span>
      </div>

      <div className="px-6 py-5">
        {/* Metrics grid */}
        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-px mb-6 border border-[var(--color-rule)]"
          style={{ background: "var(--color-rule)" }}
        >
          {[
            ["FILES PROCESSED",   result.files_processed],
            ["CHUNKS CREATED",    result.chunks_created],
            ["TABLES INGESTED",   result.tables_ingested?.length],
            ["CALCULATORS RUN",   result.calculations_run?.length],
          ].map(([label, value]) => (
            <div key={label} className="px-4 py-3" style={{ background: "var(--color-paper)" }}>
              <div
                className="numeric"
                style={{ fontSize: "1.875rem", lineHeight: 1.1, fontWeight: 400, color: "var(--color-ink)" }}
              >
                {value ?? "—"}
              </div>
              <div
                style={{
                  fontSize: "9px", fontWeight: 500, letterSpacing: "0.14em",
                  textTransform: "uppercase", color: "var(--color-ink-3)",
                  fontFamily: "var(--font-sans)", marginTop: 6,
                }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Tables ingested */}
        {result.tables_ingested?.length > 0 && (
          <div className="mb-4">
            <div
              style={{
                fontSize: "9px", fontWeight: 500, letterSpacing: "0.16em",
                textTransform: "uppercase", color: "var(--color-ink-3)",
                fontFamily: "var(--font-sans)", marginBottom: 8,
              }}
            >
              TABLES INGESTED
            </div>
            {result.tables_ingested.map((t) => (
              <div key={t} className="flex items-center justify-between py-2 border-b border-[var(--color-rule)] last:border-0">
                <Link
                  to={`/app/data/${encodeURIComponent(t)}`}
                  className="mono hover:text-[var(--color-accent)] transition-colors"
                  style={{ fontSize: "var(--text-sm)" }}
                >
                  {t}
                </Link>
                {result.rows?.[t] != null && (
                  <span style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)", fontFamily: "var(--font-mono)" }}>
                    {Number(result.rows[t]).toLocaleString()} rows
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Sheets skipped */}
        {result.sheets_skipped?.length > 0 && (
          <p style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)", marginBottom: 16 }}>
            Sheets skipped: {result.sheets_skipped.join(" · ")}
          </p>
        )}

        {/* Calculation warnings */}
        {hasCalcErrors && (
          <div
            className="px-4 py-3 mb-5 flex items-start gap-3"
            style={{ borderLeft: "3px solid var(--color-status-over)", background: "rgba(176,83,45,0.05)" }}
          >
            <AlertTriangle size={14} style={{ color: "var(--color-status-over)", flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: "9px", fontWeight: 500, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--color-status-over)", fontFamily: "var(--font-sans)", marginBottom: 4 }}>
                CALCULATION WARNINGS
              </div>
              {Object.entries(result.calculation_errors).map(([name, err]) => (
                <div key={name} className="mono" style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-2)" }}>
                  {name}: {String(err)}
                </div>
              ))}
            </div>
          </div>
        )}

        <button className="btn btn-ghost" onClick={onReset}>
          Import another file
        </button>
      </div>
    </div>
  );
}

// ── Export section ────────────────────────────────────────────────────
function ExportSection() {
  const [tables, setTables]         = useState([]);
  const [selectedTable, setSelected] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchTables()
      .then((d) => setTables(d.tables || []))
      .catch(() => {});
  }, []);

  if (tables.length === 0) return null;

  async function handleDownload() {
    if (!selectedTable) return;
    setDownloading(true);
    try {
      await downloadTable(selectedTable);
      toast.success(`${selectedTable}.xlsx downloaded`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="mt-12 pt-10 border-t border-[var(--color-rule)]">
      <div className="eyebrow mb-5">EXPORT · TABLE</div>
      <p style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)", marginBottom: 16 }}>
        Download any table — including calculated outputs — as an xlsx file.
      </p>
      <div className="flex gap-3 items-end">
        <div className="field flex-1">
          <label className="field-label">Table</label>
          <select
            className="input"
            value={selectedTable}
            onChange={(e) => setSelected(e.target.value)}
            style={{ fontFamily: "var(--font-mono)", cursor: "pointer" }}
          >
            <option value="">Select a table…</option>
            {tables.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <button
          className="btn btn-ghost"
          onClick={handleDownload}
          disabled={!selectedTable || downloading}
          style={{ flexShrink: 0 }}
        >
          <Download size={14} />
          {downloading ? "Downloading…" : "Download"}
        </button>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────
export default function ImportPage() {
  const [state, setState]     = useState("idle");   // idle | uploading | done | error
  const [step, setStep]       = useState(1);
  const [result, setResult]   = useState(null);
  const [errorMsg, setError]  = useState("");
  const [filename, setFilename] = useState("");
  const timersRef = useRef([]);

  function clearTimers() {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }

  async function handleFile(file) {
    setFilename(file.name);
    setState("uploading");
    setStep(1);
    clearTimers();

    // Simulate pipeline step progression while request is in-flight
    timersRef.current = [
      setTimeout(() => setStep(2), 3000),
      setTimeout(() => setStep(3), 9000),
    ];

    try {
      const data = await importExcel(file);
      clearTimers();
      setStep(4);
      // Brief pause so user sees "Complete" step before result card appears
      setTimeout(() => {
        setResult(data);
        setState("done");
      }, 600);
    } catch (err) {
      clearTimers();
      setError(err.message || "Import failed");
      setState("error");
    }
  }

  function reset() {
    setState("idle");
    setStep(1);
    setResult(null);
    setError("");
    setFilename("");
    clearTimers();
  }

  return (
    <div className="px-8 lg:px-12 pt-10 pb-16 max-w-3xl">
      {/* Header */}
      <div className="reveal">
        <div className="eyebrow">IMPORT · EXCEL</div>
      </div>
      <h1
        className="reveal display mt-4"
        style={{ fontSize: "var(--text-3xl)", animationDelay: "120ms" }}
      >
        Import workbook
      </h1>
      <p
        className="reveal mt-2 mb-8"
        style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)", animationDelay: "180ms" }}
      >
        Upload the SADT master workbook to embed its text for AI chat, ingest
        its structured data, and recompute all calculator outputs.
      </p>

      {/* ── Idle: drop zone ─────────────────────────────────────────── */}
      {state === "idle" && (
        <div className="reveal" style={{ animationDelay: "240ms" }}>
          <DropZone onFile={handleFile} />
        </div>
      )}

      {/* ── Uploading: pipeline stepper ─────────────────────────────── */}
      {state === "uploading" && (
        <div className="border border-[var(--color-rule)] px-7 py-6">
          <div className="flex items-center gap-2 mb-5">
            <span
              className="mono"
              style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-2)" }}
            >
              {filename}
            </span>
          </div>
          <Stepper currentStep={step} />
          <p
            className="mt-6"
            style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)" }}
          >
            This may take 15–30 seconds for large workbooks.
          </p>
        </div>
      )}

      {/* ── Complete: result card ────────────────────────────────────── */}
      {state === "done" && result && (
        <ImportResult result={result} onReset={reset} />
      )}

      {/* ── Error ────────────────────────────────────────────────────── */}
      {state === "error" && (
        <div
          className="border px-6 py-5 flex items-start gap-4"
          style={{ borderColor: "var(--color-status-over)" }}
        >
          <AlertTriangle
            size={16}
            style={{ color: "var(--color-status-over)", flexShrink: 0, marginTop: 2 }}
          />
          <div>
            <div
              style={{
                fontSize: "9px", fontWeight: 500, letterSpacing: "0.16em",
                textTransform: "uppercase", color: "var(--color-status-over)",
                fontFamily: "var(--font-sans)", marginBottom: 6,
              }}
            >
              IMPORT FAILED
            </div>
            <p style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>
              {errorMsg}
            </p>
            <button className="btn btn-ghost mt-4" onClick={reset}>
              Try again
            </button>
          </div>
        </div>
      )}

      {/* ── Export section (always visible if tables exist) ──────────── */}
      <ExportSection />
    </div>
  );
}
